/* eslint-disable */

const isFirefox = /firefox/i.test(navigator.userAgent);

const UPPER_REGEX = /[A-Z]/g;

// Don't convert these attributes from camelCase to hyphenated-attributes
const BLACKLIST = [
  'viewBox'
];

let keyCase = (key) => {
  if (BLACKLIST.indexOf(key) === -1) {
    key = key.replace(UPPER_REGEX, match => '-' + match.toLowerCase());
  }
  return key;
}

 export default {
  /**
   * The following methods are taken from mozilla/pdf.js and as such fall under
   * the Apache License (http://www.apache.org/licenses/).
   *
   * Original source can be found at mozilla/pdf.js:
   * https://github.com/mozilla/pdf.js/blob/master/web/ui_utils.js
   */

  /**
   * Approximates a float number as a fraction using Farey sequence (max order
   * of 8).
   *
   * @param {Number} x Positive float number
   * @return {Array} Estimated fraction: the first array item is a numerator,
   *                 the second one is a denominator.
   */
  approximateFraction : function(x) {
    // Fast path for int numbers or their inversions.
    if (Math.floor(x) === x) {
      return [x, 1];
    }

    const xinv = 1 / x;
    const limit = 8;
    if (xinv > limit) {
      return [1, limit];
    } else if (Math.floor(xinv) === xinv) {
      return [1, xinv];
    }

    const x_ = x > 1 ? xinv : x;
    
    // a/b and c/d are neighbours in Farey sequence.
    let a = 0, b = 1, c = 1, d = 1;
    
    // Limit search to order 8.
    while (true) {
      // Generating next term in sequence (order of q).
      let p = a + c, q = b + d;
      if (q > limit) {
        break;
      }
      if (x_ <= p / q) {
        c = p; d = q;
      } else {
        a = p; b = q;
      }
    }

    // Select closest of neighbours to x.
    if (x_ - a / b < c / d - x_) {
      return x_ === x ? [a, b] : [b, a];
    } else {
      return x_ === x ? [c, d] : [d, c];
    }
  },

  getOutputScale: function(ctx) {
    let devicePixelRatio = window.devicePixelRatio || 1;
    let backingStoreRatio = ctx.webkitBackingStorePixelRatio ||
                            ctx.mozBackingStorePixelRatio ||
                            ctx.msBackingStorePixelRatio ||
                            ctx.oBackingStorePixelRatio ||
                            ctx.backingStorePixelRatio || 1;
    let pixelRatio = devicePixelRatio / backingStoreRatio;
    return {
      sx: pixelRatio,
      sy: pixelRatio,
      scaled: pixelRatio !== 1
    };
  },

  roundToDivide: function(x, div) {
    let r = x % div;
    return r === 0 ? x : Math.round(x - r + div);
  },

    /**
   * Scale the elements of a page.
   *
   * @param {Number} pageNumber The page number to be scaled
   * @param {Object} viewport The viewport of the PDF page (see pdfPage.getViewport(scale, rotate))
   * @param {Object} context The canvas context that the PDF page is rendered to
   * @return {Array} The transform data for rendering the PDF page
   */
  scalePage: function(page, viewport, context) {
    let canvas = page.querySelector('.canvasWrapper canvas');
    let wrapper = page.querySelector('.canvasWrapper');
    let outputScale = this.getOutputScale(context);
    let transform = !outputScale.scaled ? null : [outputScale.sx, 0, 0, outputScale.sy, 0, 0];
    let sfx = this.approximateFraction(outputScale.sx);
    let sfy = this.approximateFraction(outputScale.sy);

    // Adjust width/height for scale
    page.style.visibility = '';
    canvas.width = this.roundToDivide(viewport.width * outputScale.sx, sfx[0]);
    canvas.height = this.roundToDivide(viewport.height * outputScale.sy, sfy[0]);
    canvas.style.width = this.roundToDivide(viewport.width, sfx[1]) + 'px';
    canvas.style.height = this.roundToDivide(viewport.height, sfx[1]) + 'px';
    page.style.width = `${viewport.width}px`;
    page.style.height = `${viewport.height}px`;
    // wrapper.style.width = `${viewport.width}px`;
    // wrapper.style.height = `${viewport.height}px`;

    return transform;
  },

  /**
   * Transform the rotation and scale of a node using SVG's native transform attribute.
   *
   * @param {Node} node The node to be transformed
   * @param {Object} viewport The page's viewport data
   * @return {Node}
   */
  transform: function(node, viewport) {
    let trans = this.getTranslation(viewport);

    // Let SVG natively transform the element
    node.setAttribute('transform', `scale(${viewport.scale}) rotate(${viewport.rotation}) translate(${trans.x}, ${trans.y})`);
    
    // Manually adjust x/y for nested SVG nodes
    if (!isFirefox && node.nodeName.toLowerCase() === 'svg') {
      node.setAttribute('x', parseInt(node.getAttribute('x'), 10) * viewport.scale);
      node.setAttribute('y', parseInt(node.getAttribute('y'), 10) * viewport.scale);

      let x = parseInt(node.getAttribute('x', 10));
      let y = parseInt(node.getAttribute('y', 10));
      let width = parseInt(node.getAttribute('width'), 10);
      let height = parseInt(node.getAttribute('height'), 10);
      let path = node.querySelector('path');
      let svg = path.parentNode;
     
      // Scale width/height
      [node, svg, path, node.querySelector('rect')].forEach((n) => {
        n.setAttribute('width', parseInt(n.getAttribute('width'), 10) * viewport.scale);
        n.setAttribute('height', parseInt(n.getAttribute('height'), 10) * viewport.scale);
      });

      // Transform path but keep scale at 100% since it will be handled natively
      transform(path, objectAssign({}, viewport, { scale: 1 }));
      
      switch(viewport.rotation % 360) {
        case 90:
          node.setAttribute('x', viewport.width - y - width);
          node.setAttribute('y', x);
          svg.setAttribute('x', 1);
          svg.setAttribute('y', 0);
          break;
        case 180:
          node.setAttribute('x', viewport.width - x - width);
          node.setAttribute('y', viewport.height - y - height);
          svg.setAttribute('y', 2);
          break;
        case 270:
          node.setAttribute('x', y);
          node.setAttribute('y', viewport.height - x - height);
          svg.setAttribute('x', -1);
          svg.setAttribute('y', 0);
          break;
      }
    }

    return node;
  },

  /**
   * Get the x/y translation to be used for transforming the annotations
   * based on the rotation of the viewport.
   *
   * @param {Object} viewport The viewport data from the page
   * @return {Object}
   */
  getTranslation: function(viewport) {
    let x;
    let y;

    // Modulus 360 on the rotation so that we only
    // have to worry about four possible values.
    switch(viewport.rotation % 360) {
      case 0:
        x = y = 0;
        break;
      case 90:
        x = 0;
        y = (viewport.width / viewport.scale) * -1;
        break;
      case 180:
        x = (viewport.width / viewport.scale) * -1;
        y = (viewport.height / viewport.scale) * -1;
        break;
      case 270:
        x = (viewport.height / viewport.scale) * -1;
        y = 0;
        break;
    }

    return { x, y };
  },

  /**
   * Set attributes for a node from a map
   *
   * @param {Node} node The node to set attributes on
   * @param {Object} attributes The map of key/value pairs to use for attributes
   */
  setAttributes: function(node, attributes) {
    Object.keys(attributes).forEach((key) => {
      node.setAttribute(keyCase(key), attributes[key]);
    });
  },

  getViewport: function(pdfPage, width, height) {
    const unscaledViewport = pdfPage.getViewport(1.0);
    let localScale = 1;

    localScale = Math.min(width, height) / Math.min(unscaledViewport.width, unscaledViewport.height);
    
    const localViewport = pdfPage.getViewport(localScale, 0);
    const output = pdfPage.getViewport(localViewport.scale, localViewport.rotation);
    return output;
  },

}


// WEBPACK FOOTER //
// ./sample/lib/Utils.js